// The opening is the entitlement. Never enumerate the collection to discover
// five rewards, or fall back to stale cards belonging to another opening.
const LANGUAGES = new Set(['en', 'ja', 'ko', 'zh-CN', 'de', 'fr', 'es', 'pt']);
const encoder = new TextEncoder();
const invalid = () => new Error('pack_metadata_invalid');
const text = (value, limit) => typeof value === 'string' && value.trim().length > 0
  && encoder.encode(value).byteLength <= limit;

export async function readPackOpeningCards({ api, pack, accountId, language, signal = undefined }) {
  if (!pack) return { cards: [], unavailable: [] };
  if (!text(accountId, 256) || !text(pack.id, 256) || !LANGUAGES.has(language)
    || !Array.isArray(pack.items) || pack.items.length !== 5 || typeof api?.getOpeningCard !== 'function'
    || !pack.items.every(item => text(item?.cardId, 1024))) throw invalid();
  const slots = [...new Map(pack.items.map((item, index) => [item.cardId, index])).entries()];
  const owner = new AbortController();
  const cancel = () => owner.abort();
  const check = () => { if (owner.signal.aborted) throw new DOMException('Request cancelled', 'AbortError'); };
  if (signal?.aborted) cancel();
  signal?.addEventListener('abort', cancel, { once: true });
  const results = new Array(slots.length);
  let next = 0;
  const worker = async () => {
    while (next < slots.length) {
      check();
      const position = next++, [cardId, index] = slots[position];
      const value = await api.getOpeningCard({ packId: pack.id, index, language }, { signal: owner.signal });
      check();
      if (!value || value.schemaVersion !== 1 || value.accountId !== accountId || value.packId !== pack.id
        || value.index !== index || value.cardId !== cardId
        || encoder.encode(JSON.stringify(value)).byteLength > 128 * 1024) throw invalid();
      if (value.status === 'ready') {
        if (!value.card || value.card.id !== cardId || !text(value.card.name, 128 * 1024)) throw invalid();
      } else if (!['unavailable', 'too_large'].includes(value.status) || value.card !== null) throw invalid();
      results[position] = value;
    }
  };
  try {
    // Every distinct slot at once. Each read is already bounded server-side
    // (one source, at most one translation), and the art the ceremony has to
    // preload cannot even be requested until these answer: two at a time cost
    // three round-trips before the first byte of a five-card pack.
    await Promise.all(Array.from({ length: slots.length }, worker));
    check();
    return {
      cards: results.filter(value => value.status === 'ready').map(value => value.card),
      unavailable: results.filter(value => value.status !== 'ready').map(({ cardId, status }) => ({ cardId, status })),
    };
  } finally {
    cancel(); signal?.removeEventListener('abort', cancel);
  }
}
